import { useCallback, useMemo, useRef } from "react";
import useHaklaiTableValidationController from "../../shared/table/useHaklaiTableValidationController";

// Property-parts controller: thin wrapper around the shared validation
// controller. Adds the property-parts-only concerns on top:
//   - `valueInputRefs` / `builtValueInputRefs` — column-specific DOM ref maps
//     the section uses to focus a newly-added row's first input.
// Every other concern (committed/touched state, scroll/flash, rowRefs, lock
// blur, sticky header) comes from the shared hook and behaves identically in
// both tables.

const useHaklaiPropertyPartsController = ({
  expanded,
  locked,
  parts
}) => {
  // Memoize the row-key list so the shared hook's prune effect doesn't fire
  // on every render. `parts` is already a memoized object upstream, but
  // `Object.keys` returns a fresh array each call.
  const rowKeys = useMemo(() => Object.keys(parts || {}), [parts]);

  const base = useHaklaiTableValidationController({
    rowKeys,
    locked,
    expanded,
    resetValidationOnRowKeyRemoval: true
  });

  const valueInputRefs = useRef({});
  const builtValueInputRefs = useRef({});

  const setValueInputRef = useCallback((key) => (input) => {
    if (input) { valueInputRefs.current[key] = input; return; }
    delete valueInputRefs.current[key];
  }, []);

  const setBuiltValueInputRef = useCallback((key) => (input) => {
    if (input) { builtValueInputRefs.current[key] = input; return; }
    delete builtValueInputRefs.current[key];
  }, []);

  return {
    ...base,
    setValueInputRef,
    setBuiltValueInputRef
  };
};

export default useHaklaiPropertyPartsController;
